import {
  loadExpiringDismissalsMap,
  pruneDismissalsPastExpiry,
  saveExpiringDismissalsMap,
  type ExpiringDismissalsMap,
} from './expiringDismissalsMap'
import moment from 'moment'

const SESSION_SEEN_KEY = 'labModalPopupsSeenThisSession'
const LAB_MODAL_POPUP_DISMISSALS_KEY = 'labModalPopupDismissals'
const LAB_MODAL_POPUP_DISMISSALS_VERSION = 1
const PERMANENT_DISMISSAL_END_DATE = '9999-12-31T23:59:59.999Z'

const canUseStorage = () => typeof window !== 'undefined'

const readSessionSeen = (): Set<string> => {
  if (!canUseStorage()) return new Set()
  try {
    const raw = window.sessionStorage.getItem(SESSION_SEEN_KEY)
    const parsed = raw ? JSON.parse(raw) : []
    return new Set(Array.isArray(parsed) ? parsed.filter((value): value is string => typeof value === 'string') : [])
  } catch {
    return new Set()
  }
}

const writeSessionSeen = (seenIds: Set<string>) => {
  if (!canUseStorage()) return
  try {
    window.sessionStorage.setItem(SESSION_SEEN_KEY, JSON.stringify(Array.from(seenIds)))
  } catch {
    // Storage can fail in privacy modes or when quota is exhausted. Do not block page rendering.
  }
}

const readLocalDismissals = (): ExpiringDismissalsMap => {
  if (!canUseStorage()) return {}
  try {
    return loadExpiringDismissalsMap(LAB_MODAL_POPUP_DISMISSALS_KEY, LAB_MODAL_POPUP_DISMISSALS_VERSION)
  } catch {
    return {}
  }
}

const writeLocalDismissals = (dismissals: ExpiringDismissalsMap) => {
  if (!canUseStorage()) return
  try {
    saveExpiringDismissalsMap(LAB_MODAL_POPUP_DISMISSALS_KEY, LAB_MODAL_POPUP_DISMISSALS_VERSION, dismissals)
  } catch {
    // Storage can fail in privacy modes or when quota is exhausted. Do not block page rendering.
  }
}

export function isModalPopupSuppressed(popup: App.LabModalPopupResult): boolean {
  if (popup.displayFrequency === 'oncePerSession') {
    return readSessionSeen().has(popup.id)
  }

  const dismissals = readLocalDismissals()
  const dismissal = dismissals[popup.id]
  if (!dismissal) return false

  const expiry = moment(dismissal.endDate)
  if (moment().isAfter(expiry)) {
    return false
  }

  return true
}

export function recordModalPopupSeen(popup: App.LabModalPopupResult) {
  if (popup.displayFrequency === 'oncePerSession') {
    const seenIds = readSessionSeen()
    seenIds.add(popup.id)
    writeSessionSeen(seenIds)
    return
  }

  const dismissals = readLocalDismissals()
  const now = moment()
  pruneDismissalsPastExpiry(dismissals, now)
  dismissals[popup.id] = {
    endDate: popup.displayFrequency === 'once' ? PERMANENT_DISMISSAL_END_DATE : now.clone().add(7, 'days').toISOString(),
  }
  writeLocalDismissals(dismissals)
}
